/*

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { Grid } from '@material-ui/core';
import { EntityLinksCard } from '../EntityLinksCard/EntityLinksCard';
import {
  EntityLayout,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import {
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { EntityAboutCard } from './EntityAboutCard';
import {
  CustomTechDocStyle
} from '@internal/plugin-bh-techdocs-addons';
import { MembersListCard } from '../MembersList/MembersListCard';
import { ReadmeCard } from '@axis-backstage/plugin-readme';
import ToolsVersionSupport from './InfraToolsCatalog/ToolsVersionSupport';
import { CustomEntityProvidedApisCard } from './CustomEntityProvidedApisCard/CustomEntityProvidedApisCard';
import { EntityHeaderStyles } from './EntityHeaderStyles';
import { useEffect } from 'react';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      {/* <ReportIssue /> */}
      {/* <EditDocLink /> */}
      <CustomTechDocStyle />
      {/* <BookmarkDocLink /> */}
    </TechDocsAddons>
  </EntityTechdocsContent>
);
const DocsWrapper = () => {
  useEffect(() => {
    if (window.apiDocOpen) {
      window.location.reload();
    }
  }, []);
  return <>{techdocsContent}</>;
};

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      <EntityAboutCard variant="gridItem" />
    </Grid>
    {/* <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid> */}
    {/* <Grid item md={6} xs={12}>
      <EntityLinksCard />
    </Grid> */}
    {/* <EntityRatingCard /> */}
  </Grid>
);

const serviceEntityPage = (
  <>
    <EntityHeaderStyles />
    <EntityLayout UNSTABLE_contextMenuOptions={{
      disableUnregister: 'hidden', // Hides the "Unregister entity" option
    }}>
      {/* <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
        </EntityLayout.Route> */}
      <EntityLayout.Route path="/api" title="API">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            {/* <EntityProvidedApisCard /> */}
            <CustomEntityProvidedApisCard />
          </Grid>
          {/* <Grid item md={6}>
            <EntityConsumedApisCard />
          </Grid> */}
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        {techdocsContent}
      </EntityLayout.Route>
      {/* Removed - External User*/}
      {/* <EntityLayout.Route path="/entity-feedback" title="Entity Ratings">
        <EntityFeedbackResponseContent />
      </EntityLayout.Route> */}
      {/* <EntityLayout.Route path="/readme" title="README">
        <ReadmeCard variant="fullHeight" />
      </EntityLayout.Route> */}
      {/* Removed - External User*/}
      {/* <EntityLayout.Route path="/adoption" title="Service Adoption">
        <ServiceAdoptersTable />
      </EntityLayout.Route> */}
    </EntityLayout>
  </>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/entity-feedback" title="Entity Ratings">
      <EntityFeedbackResponseContent />
    </EntityLayout.Route> */}
    <EntityLayout.Route path="/readme" title="README">
      <ReadmeCard variant="fullHeight" />
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/adoption" title="Service Adoption">
      <ServiceAdoptersTable />
    </EntityLayout.Route> */}
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <>
  <EntityHeaderStyles />
  <EntityLayout UNSTABLE_contextMenuOptions={{
      disableUnregister: 'hidden', // Hides the "Unregister entity" option
    }}>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/readme" title="README">
      <ReadmeCard variant="fullHeight" />
    </EntityLayout.Route> */}
  </EntityLayout>
  </>
);

const apiInterfaceEntityPage = (
  <>
    <EntityHeaderStyles />
    <EntityLayout UNSTABLE_contextMenuOptions={{
      disableUnregister: 'hidden', // Hides the "Unregister entity" option
    }}>
      <EntityLayout.Route path="/api" title="API">
        <CustomEntityProvidedApisCard />
      </EntityLayout.Route>

      <EntityLayout.Route path="/docs" title="Docs">
        <DocsWrapper />
      </EntityLayout.Route>
    </EntityLayout>
  </>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('api-interface')}>
      {apiInterfaceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <>
    <EntityHeaderStyles />
    <EntityLayout
      UNSTABLE_contextMenuOptions={{
        disableUnregister: 'hidden', // Hides the "Unregister entity" option
      }}
    >
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3}>
          {entityWarningContent}
          <Grid item md={6}>
            <EntityAboutCard />
          </Grid>
          {/* <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>  */}
        </Grid>
      </EntityLayout.Route>
    </EntityLayout>
  </>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12}>
          <MembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <>
  <EntityHeaderStyles />
  <EntityLayout UNSTABLE_contextMenuOptions={{
    disableUnregister: 'hidden', // Hides the "Unregister entity" option
  }}>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        {/* <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid> */}
        {/* <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid> */}
        {/* <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid> */}
      </Grid>
    </EntityLayout.Route>
    {/* <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route> */}
  </EntityLayout>
  </>
);

const domainPage = (
  <>
    <EntityHeaderStyles />
    <EntityLayout
      UNSTABLE_contextMenuOptions={{
        disableUnregister: 'hidden', // Hides the "Unregister entity" option
      }}
    >
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3} alignItems="stretch">
          {entityWarningContent}
          <Grid item md={6}>
            <EntityAboutCard variant="gridItem" />
          </Grid>
          {/* <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid> */}
        </Grid>
      </EntityLayout.Route>
    </EntityLayout>
  </>
);

const infraServiceCatalog = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const infraToolsCatalog = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/version-support" title="Version Support">
      <ToolsVersionSupport />
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  
  <EntitySwitch>
    
    <EntitySwitch.Case
      if={
        isKind('component') &&
        (isComponentType('constructs'))
      }
    >
      {infraServiceCatalog}
    </EntitySwitch.Case>

    <EntitySwitch.Case
      if={
        isKind('component') &&
        (isComponentType('stamps'))
      }
    >
      {infraServiceCatalog}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('component') && isComponentType('tools')}>
      {infraToolsCatalog}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('component')}>
      {componentPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('api')}>
      {apiPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('group')}>
      {groupPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('user')}>
      {userPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('system')}>
      {systemPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isKind('domain')}>
      {domainPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
